import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FuelReport } from "types/FuelReport";
import { fetchFuelReportById } from "services/fuel-report.service";

// Define the types for your initial state
interface FuelReportContainerState {
  isLoading: boolean;
  isError: boolean;
  errMsg: string;
  fuelReportEntity: FuelReport | null;
  // The report id of the latest in-flight request. Responses (success or error)
  // only apply if they still match it, so a stale response for a report we've
  // navigated away from can't overwrite the current one or surface a wrong error.
  requestedReportId: string | null;
}

// Define the initial state
const initialState: FuelReportContainerState = {
  isLoading: false,
  isError: false,
  errMsg: "",
  fuelReportEntity: null,
  requestedReportId: null,
};

// Create the slice
const FuelReportContainerSlice = createSlice({
  name: "fuelReportContainer",
  initialState,
  reducers: {
    setDestroy() {
      return initialState;
    },
    setLoading(
      state: FuelReportContainerState,
      action: PayloadAction<boolean>
    ) {
      state.isLoading = action.payload;
    },
    // Marks the start of a fetch: records which report is requested, shows
    // loading, and clears any prior error so a stale error can't block the new
    // report.
    setRequest(
      state: FuelReportContainerState,
      action: PayloadAction<string>
    ) {
      state.requestedReportId = action.payload;
      state.isLoading = true;
      state.isError = false;
      state.errMsg = "";
    },
    setError(
      state: FuelReportContainerState,
      action: PayloadAction<{ id: string; error: string }>
    ) {
      if (state.requestedReportId !== action.payload.id) {
        return;
      }
      state.isLoading = false;
      state.isError = true;
      state.errMsg = action.payload.error;
    },
    setFuelReportContainer(
      state: FuelReportContainerState,
      action: PayloadAction<{ id: string; data: any }>
    ) {
      if (state.requestedReportId !== action.payload.id) {
        return;
      }
      state.fuelReportEntity = action.payload.data;
      state.isLoading = false;
      state.isError = false;
      state.errMsg = "";
    },
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = FuelReportContainerSlice;

// Extract and export each action creator by name
export const {
  setDestroy,
  setLoading,
  setRequest,
  setError,
  setFuelReportContainer,
} = actions;

// Export the reducer as default
export default reducer;

// Define the type for the dispatch function
export const fetchFuelReportDetails =
  ({ reportId, companyId }: { reportId: string; companyId: string }) =>
  async (dispatch: any) => {
    dispatch(setRequest(reportId));
    try {
      const fuelReportEntity = await fetchFuelReportById(reportId, companyId);
      dispatch(
        setFuelReportContainer({ id: reportId, data: fuelReportEntity })
      );
    } catch (e) {
      const error = "not_fetch";
      dispatch(setError({ id: reportId, error }));
      return false;
    }
  };
